/**
 * Unified route policy guard for platform_mode based access.
 * Route policy lives in route data as `allowedPlatformModes`.
 */
import { Injectable } from '@angular/core';
import {
    ActivatedRouteSnapshot,
    CanActivate,
    CanMatch,
    Route,
    Router,
    RouterStateSnapshot,
    UrlSegment,
    UrlTree,
} from '@angular/router';
import { PlatformMode } from '@app/core/models/platform-mode.model';
import { LoggingService } from '@app/core/services';
import { AuthService } from '@modules/auth/services';
import { PlatformRouteAccess } from '@modules/navigation/constants/platform-access-policy.constants';
import { AccountAccessService } from '@modules/navigation/services';

type PlatformAccessRouteData = {
    allowedPlatformModes?: PlatformRouteAccess;
};

@Injectable({ providedIn: 'root' })
export class PlatformAccessGuard implements CanActivate, CanMatch {
    constructor(
        private readonly accountAccessService: AccountAccessService,
        private readonly authService: AuthService,
        private readonly loggingService: LoggingService,
        private readonly router: Router
    ) {}

    async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
        return this.evaluateAccess(route.data, state.url);
    }

    async canMatch(route: Route, segments: UrlSegment[]): Promise<boolean | UrlTree> {
        const url = `/${segments.map((segment) => segment.path).join('/')}`;
        return this.evaluateAccess(route.data, url);
    }

    private async evaluateAccess(data: PlatformAccessRouteData | undefined, url: string): Promise<boolean | UrlTree> {
        const securityContextLoaded = await this.authService.ensureSecurityContextLoaded();
        const allowedPlatformModes = data?.allowedPlatformModes;
        const platformMode = this.accountAccessService.getPlatformMode();

        if (
            securityContextLoaded &&
            this.hasRoutePolicy(allowedPlatformModes) &&
            platformMode &&
            allowedPlatformModes.includes(platformMode)
        ) {
            return true;
        }

        this.loggingService.warn('[PlatformAccessGuard] Route access denied.', {
            allowedPlatformModes: allowedPlatformModes ?? 'missing',
            platformMode: platformMode ?? 'unknown',
            securityContextLoaded,
            url,
        });
        return this.router.parseUrl(this.accountAccessService.getAccessDeniedRoute());
    }

    private hasRoutePolicy(policy: PlatformRouteAccess | undefined): policy is readonly PlatformMode[] {
        return Array.isArray(policy) && policy.length > 0;
    }
}
